import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAppContext } from '../../context/AppContext';
import {
    FaThLarge, FaUsers, FaUserAstronaut, FaWeight,
    FaMedal, FaCog, FaSignOutAlt, FaTimes,
    FaFileInvoiceDollar, FaTv, FaListAlt, FaTrophy,
    FaChevronDown, FaUserTie
} from 'react-icons/fa';

const AdminSidebar = ({ isOpen, toggleSidebar, logout, isDesktop }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const { user } = useAppContext();

    // State untuk mengontrol Dropdown / Submenu (collapse)
    const [openMenus, setOpenMenus] = useState({});

    const allowedAdminRoles = ['super_admin', 'admin_timbang', 'admin_medali', 'admin_koordinator'];

    useEffect(() => {
        if (user && !allowedAdminRoles.includes(user.role)) {
            navigate('/');
        }
    }, [user, navigate]);

    // Fungsi Toggle Menu (Untuk Mobile Click)
    const toggleMenu = (menuId) => {
        if (!isDesktop) {
            setOpenMenus(prev => ({ ...prev, [menuId]: !prev[menuId] }));
        }
    };

    // Struktur Menu Lengkap dengan Submenu
    const menuStructure = [
        { id: 'dashboard', title: 'Dashboard', path: '/admin/dashboard', icon: <FaThLarge />, roles: ['super_admin', 'admin_koordinator'] },
        { id: 'pembayaran', title: 'Verifikasi Bayar', path: '/admin/pembayaran', icon: <FaFileInvoiceDollar />, roles: ['super_admin'] },
        {
            id: 'atlet_kyorugi', title: 'Atlet Kyorugi', icon: <FaUserAstronaut />, roles: ['super_admin', 'admin_koordinator'],
            subMenus: [
                { title: 'Prestasi', path: '/admin/atlet/kyorugi/prestasi' },
                { title: 'Festival', path: '/admin/atlet/kyorugi/festival' }
            ]
        },
        {
            id: 'atlet_poomsae', title: 'Atlet Poomsae', icon: <FaUserAstronaut />, roles: ['super_admin', 'admin_koordinator'],
            subMenus: [
                { title: 'Prestasi', path: '/admin/atlet/poomsae/prestasi' },
                { title: 'Festival', path: '/admin/atlet/poomsae/festival' }
            ]
        },
        { id: 'tim', title: 'Data Tim', path: '/admin/tim', icon: <FaUsers />, roles: ['super_admin'] },
        {
            id: 'penimbangan', title: 'Penimbangan', icon: <FaWeight />, roles: ['super_admin', 'admin_timbang'],
            subMenus: [
                { title: 'Prestasi', path: '/admin/timbang/prestasi' },
                { title: 'Festival', path: '/admin/timbang/festival' }
            ]
        },
        {
            id: 'bagan', title: 'Bagan & Jadwal', icon: <FaTrophy />, roles: ['super_admin', 'admin_koordinator'],
            subMenus: [
                { title: 'Prestasi', path: '/admin/bagan/prestasi' },
                { title: 'Festival', path: '/admin/bagan/festival' }
            ]
        },
        {
            id: 'nilai_poomsae', title: 'Penilaian Poomsae', icon: <FaListAlt />, roles: ['super_admin', 'admin_koordinator', 'admin_medali'],
            subMenus: [
                { title: 'Prestasi', path: '/admin/poomsae/prestasi' },
                { title: 'Festival', path: '/admin/poomsae/festival' }
            ]
        },
        {
            id: 'live_monitor', title: 'Live Monitor', icon: <FaTv />, roles: ['super_admin', 'admin_koordinator'],
            subMenus: [
                { title: 'Paparan Prestasi', path: '/admin/live-paparan/prestasi' },
                { title: 'Paparan Festival', path: '/admin/live-paparan/festival' }
            ]
        },
        { id: 'medali', title: 'Medali & Juara', path: '/admin/medali', icon: <FaMedal />, roles: ['super_admin', 'admin_medali'] },

        { id: 'pengguna', title: 'Data Pengguna', path: '/admin/users', icon: <FaUsers />, roles: ['super_admin'] },
        { id: 'pengaturan', title: 'Pengaturan', path: '/admin/settings', icon: <FaCog />, roles: ['super_admin'] },
        { id: 'profile', title: 'Profile', path: '/admin/profile', icon: <FaUserTie />, roles: ['super_admin', 'admin_timbang', 'admin_medali', 'admin_koordinator'] },
    ];

    const sidebarTransform = isOpen ? 'translateX(0)' : 'translateX(-100%)';

    if (!user || !allowedAdminRoles.includes(user.role)) return null;

    // Komponen helper untuk merender single link vs dropdown
    const renderMenuItem = (item) => {
        // PENGAMANAN: Mencegah crash jika roles kosong
        const itemRoles = item.roles || [];
        if (!itemRoles.includes(user.role)) return null;

        // Cek apakah parent aktif (salah satu childnya sedang diakses)
        const isParentActive = item.subMenus?.some(sub => location.pathname.startsWith(sub.path)) || location.pathname === item.path;
        // Status terbuka dari State (mobile) atau CSS hover (desktop)
        const isOpenState = openMenus[item.id];

        if (item.subMenus) {
            // Render Dropdown
            return (
                <li key={item.id} className="nav-item dropdown-custom">
                    <div
                        className="nav-link d-flex align-items-center gap-3 text-white transition-all w-100"
                        style={{
                            borderRadius: '12px', padding: '12px 16px',
                            background: isParentActive ? 'rgba(255, 255, 255, 0.15)' : 'transparent',
                            boxShadow: isParentActive ? '0 4px 12px rgba(0,0,0,0.1)' : 'none',
                            fontWeight: isParentActive ? '700' : '500', opacity: isParentActive ? 1 : 0.8,
                            cursor: 'pointer'
                        }}
                        onClick={() => toggleMenu(item.id)}
                    >
                        <span style={{ fontSize: '1.2rem', width: '24px', display: 'flex', justifyContent: 'center' }}>{item.icon}</span>
                        <span>{item.title}</span>
                        <FaChevronDown className={`ms-auto transition-transform ${isOpenState ? 'rotate-180' : ''}`} style={{ fontSize: '12px' }} />
                    </div>

                    {/* Submenu Container */}
                    <div className={`submenu-collapse ${isOpenState ? 'show' : ''}`}>
                        <ul className="nav flex-column ms-4 ps-2 border-start border-white border-opacity-10 mt-1 gap-1">
                            {item.subMenus.map((sub, idx) => {
                                const isSubActive = location.pathname === sub.path;
                                return (
                                    <li className="nav-item" key={idx}>
                                        <Link
                                            to={sub.path}
                                            onClick={() => !isDesktop && toggleSidebar()}
                                            className={`nav-link text-white py-2 px-3 rounded-3 small ${isSubActive ? 'fw-bold' : 'text-white-50'}`}
                                            style={{
                                                background: isSubActive ? 'rgba(255,255,255,0.1)' : 'transparent',
                                                transition: 'background 0.2s'
                                            }}
                                            onMouseEnter={(e) => !isSubActive && (e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)')}
                                            onMouseLeave={(e) => !isSubActive && (e.currentTarget.style.background = 'transparent')}
                                        >
                                            {sub.title}
                                        </Link>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                </li>
            );
        }

        // Render Normal Link
        return (
            <li key={item.id} className="nav-item">
                <Link
                    to={item.path}
                    className={`nav-link d-flex align-items-center gap-3 text-white transition-all`}
                    style={{
                        borderRadius: '12px', padding: '12px 16px',
                        background: isParentActive ? 'rgba(255, 255, 255, 0.15)' : 'transparent',
                        boxShadow: isParentActive ? '0 4px 12px rgba(0,0,0,0.1)' : 'none',
                        fontWeight: isParentActive ? '700' : '500', opacity: isParentActive ? 1 : 0.8
                    }}
                    onClick={() => !isDesktop && toggleSidebar()}
                    onMouseEnter={(e) => !isParentActive && (e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)')}
                    onMouseLeave={(e) => !isParentActive && (e.currentTarget.style.background = 'transparent')}
                >
                    <span style={{ fontSize: '1.2rem', width: '24px', display: 'flex', justifyContent: 'center' }}>{item.icon}</span>
                    <span>{item.title}</span>
                </Link>
            </li>
        );
    };

    return (
        <>
            {/* Mobile Overlay */}
            {!isDesktop && isOpen && (
                <div
                    className="position-fixed top-0 start-0 w-100 h-100 bg-dark"
                    style={{ opacity: 0.5, zIndex: 2 }}
                    onClick={toggleSidebar}
                ></div>
            )}

            {/* Sidebar Asli - Tema Gelap Konsisten */}
            <aside
                className="position-fixed top-0 start-0 h-100 text-white d-flex flex-column z-3"
                style={{
                    width: '280px',
                    background: 'linear-gradient(180deg, #312e81 0%, #1e1b4b 100%)',
                    transform: sidebarTransform,
                    transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    boxShadow: '4px 0 24px rgba(0,0,0,0.1)',
                    zIndex: 1050
                }}
            >
                {/* Header Sidebar */}
                <div className="p-4 d-flex align-items-center justify-content-between border-bottom border-white border-opacity-10">
                    <Link to="/admin/dashboard" className="text-white text-decoration-none d-flex align-items-center gap-3">
                        <div className="bg-white text-primary rounded p-2 d-flex align-items-center justify-content-center shadow-sm" style={{ width: '40px', height: '40px' }}>
                            <FaThLarge size={20} />
                        </div>
                        <div>
                            <span className="d-block fw-bold text-uppercase fs-6" style={{ letterSpacing: '1px' }}>Admin Panel</span>
                            <span className="d-block text-white-50" style={{ fontSize: '0.65rem' }}>Management System</span>
                        </div>
                    </Link>

                    {!isDesktop && (
                        <button className="btn btn-sm btn-link text-white p-0" onClick={toggleSidebar}>
                            <FaTimes size={20} />
                        </button>
                    )}
                </div>

                {/* Menu List */}
                <div className="flex-grow-1 overflow-auto p-3 custom-scrollbar">
                    <small className="text-uppercase text-white-50 fw-bold px-3 mb-2 d-block" style={{ fontSize: '0.7rem', letterSpacing: '1px' }}>
                        Menu ({user.role.replace('_', ' ')})
                    </small>
                    <ul className="nav flex-column gap-2 pb-4">
                        {menuStructure.map(renderMenuItem)}
                    </ul>
                </div>

                {/* Footer Logout */}
                <div className="p-3 border-top border-white border-opacity-10 mt-auto bg-black bg-opacity-25">
                    <div className="px-3 mb-3 small text-white-50">
                        Logged in as: <br />
                        <span className="text-white fw-bold">{user.name}</span>
                    </div>
                    <button
                        onClick={logout}
                        className="btn w-100 d-flex align-items-center justify-content-center gap-2 text-white bg-danger bg-opacity-75 hover-scale shadow-sm"
                        style={{ borderRadius: '12px', padding: '12px', transition: 'all 0.2s', border: 'none' }}
                    >
                        <FaSignOutAlt /> Log Out
                    </button>
                </div>

                {/* CSS Khusus Sidebar (Termasuk logika hover desktop) */}
                <style>{`
                    .hover-scale { transition: transform 0.2s; }
                    .hover-scale:hover { transform: scale(1.02); }
                    .custom-scrollbar::-webkit-scrollbar { width: 6px; }
                    .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
                    .custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.2); border-radius: 10px; }
                    .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,0.4); }
                    
                    /* Logika Collapse */
                    .submenu-collapse {
                        max-height: 0;
                        overflow: hidden;
                        transition: max-height 0.3s ease-in-out;
                    }
                    .submenu-collapse.show {
                        max-height: 200px;
                    }
                    .transition-transform { transition: transform 0.3s ease; }
                    .rotate-180 { transform: rotate(180deg); }

                    /* Hover otomatis hanya di Desktop (>= 992px) */
                    @media (min-width: 992px) {
                        .dropdown-custom:hover .submenu-collapse {
                            max-height: 200px;
                        }
                        .dropdown-custom:hover .rotate-180 {
                            transform: rotate(180deg);
                        }
                    }
                `}</style>
            </aside>
        </>
    );
};

export default AdminSidebar;